import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DashboardLayout } from "../components/layout";
import Icon from "../components/common/Icon";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";
import { useNavigate } from "react-router-dom";
import CreatePostModal from "../components/post/CreatePostModal";
import { getAssetUrl, Pagination } from "../services/helper";
const API_ROOT = (process.env.REACT_APP_API_URL || "http://localhost:5000/api").replace("/api", "");

const FaithStreamPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [activeCategory, setActiveCategory] = useState("all");
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [page, setPage] = useState(1);

  const isPremium = user?.role === "pilgrim" || user?.role === "admin";

  // Fetch Categories
  const { data: categoriesData } = useQuery({
    queryKey: ["mediaCategories"],
    queryFn: () => api.getStreamCategoriesData(), // Adjust endpoint if needed
  });

  const categories = [
    { id: "all", label: "All Media" },
    ...(categoriesData?.data?.map(cat => ({
      id: cat.id.toString(),
      label: cat.name,
    })) || []),
  ];
  const isVideoFile = (url) => getFileType(url) === 'video';

  // Fetch Media
  const { data: mediaData, isLoading } = useQuery({
    queryKey: ["faithStreamMedia", page, activeCategory],
    queryFn: () => api.getMediaItems(page, 9, "", activeCategory === "all" ? "" : activeCategory),
    staleTime: 5 * 60 * 1000,
  });

  const media = mediaData?.data || mediaData?.media || [];
  const pagination = mediaData?.pagination;

  // Toggle Save Mutation
  const toggleSaveMutation = useMutation({
    mutationFn: async (mediaId) => {
      const res = await api.toggleSaveStreamMedia(mediaId);
      return { mediaId, saved: res.saved };
    },
    onSuccess: (result) => {
      queryClient.setQueryData(["faithStreamMedia", page, activeCategory], (old) => ({
        ...old,
        data: old.data.map(item =>
          item.id === result.mediaId ? { ...item, is_saved: result.saved } : item
        ),
      }));
    },
  });

  const handleSaveToggle = (item) => {
    toggleSaveMutation.mutate(item.id);
  };

  const MediaModal = ({ item, onClose }) => {

    return (
      <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={onClose}>
        <div className="bg-black rounded-2xl overflow-hidden max-w-4xl w-full relative" onClick={e => e.stopPropagation()}>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 bg-white/20 hover:bg-white/40 text-white rounded-full px-4 py-2 text-sm"
          >
            ✕ Close
          </button>

          <MediaRenderer url={item.media_url} title={item.title} apiRoot={API_ROOT} />

          <div className="p-5 bg-white text-black">
            <h3 className="font-bold text-xl">{item.title}</h3>
            <p className="text-gray-600 mt-1">{item.author}</p>
          </div>
        </div>
      </div>
    );
  };

  const MediaRenderer = ({ url, title }) => {
    const fileType = getFileType(url);
    const mediaSrc = url?.startsWith("/uploads") ? `${API_ROOT}${url}` : url;

    switch (fileType) {
      case 'video':
        return <video src={mediaSrc} controls className="w-full max-h-[80vh] bg-black rounded-xl" />;
      case 'audio':
        return <audio src={mediaSrc} controls className="w-full" />;
      case 'image':
        return <img src={mediaSrc} alt={title} className="w-full max-h-[80vh] object-contain" />;
      default:
        return <div className="p-8 text-white text-center">Unsupported media</div>;
    }
  };

  const getFileType = (url) => {
    const ext = url?.toLowerCase().substring(url.lastIndexOf('.'));
    if (['.mp4', '.webm', '.mov'].includes(ext)) return 'video';
    if (['.mp3', '.wav', '.m4a'].includes(ext)) return 'audio';
    if (['.jpg', '.jpeg', '.png', '.gif'].includes(ext)) return 'image';
    return 'unknown';
  };

  return (
    <DashboardLayout onCreatePost={() => setShowCreate(true)}>
      <div className="flex-1 min-w-0 py-4 sm:py-6 px-4 sm:px-8 md:px-6 lg:px-[20px] 2xl:px-[70px] overflow-y-auto" style={{ maxHeight: "calc(100vh - 56px)" }}>

        <h1 className="text-[22px] sm:text-[28px] font-black text-gray-900 mb-5 font-body">Faith Stream</h1>

        {/* Dynamic Categories */}
        <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-1">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`px-5 py-2 rounded-full text-[13px] sm:text-[14px] font-medium transition-all whitespace-nowrap flex-shrink-0 ${activeCategory === cat.id
                ? "bg-primary text-white"
                : "text-gray-500 hover:bg-gray-100"
                }`}
            >
              {cat.label}
            </button>
          ))}
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : media.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-gray-400 text-[15px]">No Faith Stream available.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 pb-6">
              {media.map((item) => (
                <div
                  key={item.id}
                  className="group cursor-pointer bg-white p-4 rounded-2xl hover:shadow-md transition-all"
                  onClick={() => setSelectedMedia(item)}
                >
                  <div className="relative rounded-xl overflow-hidden mb-3 aspect-[16/9] bg-gray-100">
                    <img
                      src={getAssetUrl(item.thumbnail)}
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    {isVideoFile(item.media_url) && (
                      <div className="absolute inset-0 flex items-center justify-center opacity-80 group-hover:opacity-100 transition-opacity">
                        <div className="w-11 h-11 bg-white/80 rounded-full flex items-center justify-center shadow-md">
                          <Icon name="play" size={18} className="text-gray-700 ml-0.5" />
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    <h3 className="text-[13px] sm:text-[14px] font-bold text-gray-900 leading-snug mb-1 font-body">
                      {item.title}
                    </h3>
                    <div className="flex items-center justify-between">
                      <p className="text-[10px] sm:text-[11px] text-gray-400 font-semibold tracking-[1px] uppercase">
                        {item.author}
                      </p>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleSaveToggle(item); }}
                        className="flex items-center gap-1 text-[11px] font-bold text-black hover:underline"
                      >
                        <Icon
                          name={item.is_saved ? "bookmarkOff" : "bookmark"}
                          size={14}
                          className="text-black"
                        />
                        {item.is_saved ? "Unsave" : "Save"}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="pt-6 border-t mt-4">
          <Pagination pagination={pagination} onPageChange={setPage} />
        </div>
      </div>

      {/* Media Modal */}
      {selectedMedia && (
        <MediaModal
          item={selectedMedia}
          onClose={() => setSelectedMedia(null)}
        />
      )}

      <CreatePostModal isOpen={showCreate} onClose={() => setShowCreate(false)} />
    </DashboardLayout>
  );
};

/* Media Modal */


export default FaithStreamPage;